import React, { FunctionComponent, useState, useEffect, ChangeEvent } from 'react';
import { debounce } from 'debounce';
import { FormattedMessage } from 'react-intl';
import { FormControl } from 'react-bootstrap';
import { connect } from 'react-redux';
import { find } from 'ramda';
import { IBindingAction, IBindingCallback1 } from '../../common/models/callback';
import { IPositionFilter } from '../../common/models/filter/IPositionFilter';
import { ISelectedPosition } from '../../common/models/position/ISelectedPosition';
import { IReferenceReqPosition } from '../../common/models/position/IReferenceReqPosition';
import { getDescription } from './utils';
import Icon from '../../components/Icon';
import Table from '../../components/Table';
import { findAllPositionsRoutine, fetchRecentPositionsRoutine } from './routines';
import { IAppState } from '../../common/models/store/IAppState';
import styles from './styles.module.scss';
import Illustration from '../../components/Illustration';
import { getPositionColumns } from '../../common/helpers/columnsHelper';

interface IProps {
  fetchPositions: IBindingCallback1<IPositionFilter>;
  fetchRecentPositions: IBindingAction;
  setPosition: IBindingCallback1<ISelectedPosition>;
  selectedPosition: ISelectedPosition | null;
  positions: IReferenceReqPosition[];
  simpleVersion?: boolean;
  total: number;
}

const ChoosePositionPage: FunctionComponent<IProps> = ({
  total,
  positions,
  simpleVersion,
  selectedPosition,
  setPosition,
  fetchPositions,
  fetchRecentPositions
}) => {
  const [filter, setFilter] = useState({
    from: 0,
    to: 10,
    name: selectedPosition?.name || ''
  });

  const onSearchChange = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    setPosition({ name: currentTarget.value });
    setFilter({ ...filter, name: currentTarget.value });
  };

  const search = debounce((searchFilter: IPositionFilter) => fetchPositions(searchFilter), 500);

  const onSelect = (item: IReferenceReqPosition, isSelected: boolean) => (isSelected
    ? setPosition({ id: item.id, name: item.name })
    : setPosition({ name: selectedPosition?.name as string }));

  const onLoadMore = (from: number, to: number) => fetchPositions({ ...filter, from, to });

  useEffect(() => {
    if (filter.name.trim()) {
      search(filter);
    } else if (!filter.name.trim()) {
      fetchRecentPositions();
    }
    return () => search.clear();
  }, [filter]);

  const description = getDescription(filter.name);
  const defaultSelected = find(({ id }) => id === selectedPosition?.id, positions);

  const noDataPlaceholder = filter.name ? (
    <Illustration
      variant="binoculars"
      height="18rem"
      header={(
        <FormattedMessage
          id="step1.noPositions"
          defaultMessage="Add new position?"
        />
      )}
      description={(
        <FormattedMessage
          id="step1.noPositionsHint"
          defaultMessage="No matching positions found. Click “Next step” to add this as a new position!"
        />
      )}
    />
  ) : <></>;

  return (
    <div className={`${styles.step1} ${simpleVersion ? styles.simpleV : ''}`}>
      <div className={styles.searchContainer}>
        <div className={styles.label}>
          <FormattedMessage
            id="step1.searchLabel"
            defaultMessage="What position is the candidate applying for?"
          />
        </div>
        <FormattedMessage id="step1.title" defaultMessage="Position title">
          {placeholder => (
            <div className="position-relative">
              <FormControl
                placeholder={placeholder?.toString()}
                onChange={onSearchChange}
                bsPrefix={styles.searchInput}
                aria-describedby="basic-addon1"
                value={selectedPosition?.name || filter.name}
                autoFocus
              />
              {selectedPosition?.id && <Icon icon="check" className={styles.inputIcon} /> }
            </div>
          )}
        </FormattedMessage>
      </div>
      <div className={styles.step1Body}>
        <p className={styles.searchDescription}>
          <FormattedMessage id={description.id} defaultMessage={description.defaultMessage} />
        </p>
        <div className={styles.step1TableWrp}>
          <Table
            config={{
              data: { items: positions, total },
              columns: getPositionColumns(filter.name.trim(), Boolean(simpleVersion)),
              onLoad: onLoadMore,
              onRowSelected: onSelect,
              rowSelection: 'single',
              selectionType: 'combine',
              defaultSelected: defaultSelected && [defaultSelected],
              noDataPlaceholder
            }}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({
  choosePosition: { positions, total }
}: IAppState) => ({ positions, total });

const mapDispatchToProps = {
  fetchRecentPositions: fetchRecentPositionsRoutine,
  fetchPositions: findAllPositionsRoutine
};

export default connect(mapStateToProps, mapDispatchToProps)(ChoosePositionPage);
